import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { SettingsHeader } from './components/settings-header';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { MatterType } from '../../../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useUpdateMatter from '@/api/mutations/useUpdateMatter';
import { useGetMatterRuns } from '@/api/queries/useGetMatterRuns';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useUserContext } from '@/Contexts/User';
import formatDate from '@/helpers/formatDate';
import { hasAnyOngoingRun } from '@/helpers/runs';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/new/components/shadcn/form';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { DeleteMatterDialog } from '@/new/pages/matters/components/delete-matter-dialog';

const matterNameSchema = z.object({
  name: z
    .string()
    .min(1, { message: 'Matter name must be provided.' })
    .refine((val) => val.trim().length > 0, { message: 'Matter name cannot be empty or just spaces.' }),
});

const matterCodeSchema = z.object({
  code: z.string().optional(),
});

type MatterNameForm = z.infer<typeof matterNameSchema>;
type MatterCodeForm = z.infer<typeof matterCodeSchema>;

export function Metadata() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();
  const { data: matter } = useGetMatterV2(matterId);
  const { data: runs } = useGetMatterRuns(matterId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { mutateAsync: updateMatter, isLoading } = useUpdateMatter(matterId);

  // Check if this is a personal matter
  const isPersonalMatter = matter?.type === MatterType.Personal;

  // Check if user can delete this matter
  const isCreator = matter?.createdById === user?.id;
  const hasCaseDeleteAnyPermission = hasPermission(Permission.CaseDeleteAny);
  const canDelete = isCreator || hasCaseDeleteAnyPermission;

  // Check if there are any ongoing runs
  const hasOngoingRuns = hasAnyOngoingRun(runs);

  const form = useForm<MatterNameForm>({
    resolver: zodResolver(matterNameSchema),
    defaultValues: {
      name: '',
    },
  });

  const codeForm = useForm<MatterCodeForm>({
    resolver: zodResolver(matterCodeSchema),
    defaultValues: {
      code: '',
    },
  });

  const onEditMatterName = async (data: MatterNameForm) => {
    trackCustomEvent(TrackingEventsV2.UpdateMatter, { matterId });
    await updateMatter({ name: data.name.trim() }).catch(() => {
      toast.error('Failed to update matter name');
    });
    setIsModalOpen(false);
  };

  const onEditMatterCode = async (data: MatterCodeForm) => {
    trackCustomEvent(TrackingEventsV2.UpdateMatter, { matterId });
    await updateMatter({ code: data.code?.trim() || undefined }).catch(() => {
      toast.error('Failed to update matter code');
    });
    setIsCodeModalOpen(false);
  };

  useEffect(() => {
    if (matter?.name) {
      form.reset({ name: matter.name });
    }
  }, [matter?.name, form]);

  useEffect(() => {
    if (matter) {
      codeForm.reset({ code: matter.code ?? '' });
    }
  }, [matter?.code, codeForm]);

  if (!matter) return null;

  return (
    <>
      <SettingsHeader
        title="Matter Metadata"
        subheading={<p className="text-sm text-gray-500">View and update your matter name and metadata information.</p>}
      />
      <div className="mb-6 space-y-2">
        <Label className="text-sm font-medium text-gray-700">Matter Name</Label>
        <div className="flex items-center gap-8">
          <div className="text-sm font-medium text-gray-900">{matter.name}</div>
          {!isPersonalMatter && (
            <Button variant="outline" size="sm" onClick={() => setIsModalOpen(true)}>
              <Icon className="lucide--pencil-line h-3 w-3" />
              Update Name
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">Matter Code</Label>
            <div className="flex items-center gap-8">
              <div className="font-mono text-sm text-gray-600">{matter.code || 'Not set'}</div>
              {!isPersonalMatter && (
                <Button variant="outline" size="sm" onClick={() => setIsCodeModalOpen(true)}>
                  <Icon className="lucide--pencil-line h-3 w-3" />
                  {matter.code ? 'Update Code' : 'Add Code'}
                </Button>
              )}
            </div>
          </div>

          {matter.createdDate && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Created</Label>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Icon className="lucide--calendar h-4 w-4 text-gray-400" />
                <span>{formatDate(matter.createdDate, true, true)}</span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">Created By</Label>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Icon className="lucide--user h-4 w-4 text-gray-400" />
              <span>{matter.createdBy?.email || 'Unknown'}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">Matter ID</Label>
            <div className="font-mono text-sm text-gray-600">{matter.id}</div>
          </div>
        </div>

        {matter.description && (
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">Description</Label>
            <div className="text-sm text-gray-600">{matter.description}</div>
          </div>
        )}
      </div>

      {!isPersonalMatter && (
        <div className="mt-10 border-t border-gray-200 pt-6">
          <h3 className="mb-2 text-base font-medium text-gray-700">Delete Matter</h3>

          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Permanently delete this matter, all subsets, documents, and associated data.
            </p>
            {canDelete && !hasOngoingRuns ? (
              <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
                <Icon className="lucide--trash-2 h-3 w-3" />
                Delete Matter
              </Button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button variant="destructive" disabled>
                      <Icon className="lucide--trash-2 h-3 w-3" />
                      Delete Matter
                    </Button>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-56">
                  <p className="text-center">
                    {hasOngoingRuns
                      ? 'Cannot delete matter while processing is ongoing'
                      : 'Only the matter creator or users with admin permissions can delete this matter'}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      )}

      <DeleteMatterDialog
        matterId={matterId}
        matterName={matter.name}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        redirectOnSuccess
      />

      <Dialog
        open={isModalOpen}
        onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open && matter) {
            form.reset({ name: matter.name });
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center gap-2">
                <Icon className="lucide--edit-2 h-4 w-4 text-gray-700" />
                Update Matter Name
              </div>
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onEditMatterName)}>
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">Matter Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter matter name..." className="w-full" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsModalOpen(false);
                    form.reset({ name: matter.name });
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={isLoading}>
                  {isLoading ? 'Updating...' : 'Update Name'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCodeModalOpen}
        onOpenChange={(open) => {
          setIsCodeModalOpen(open);
          if (!open && matter) {
            codeForm.reset({ code: matter.code || '' });
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center gap-2">
                <Icon className="lucide--edit-2 h-4 w-4 text-gray-700" />
                {matter.code ? 'Update Matter Code' : 'Add Matter Code'}
              </div>
            </DialogTitle>
          </DialogHeader>
          <Form {...codeForm}>
            <form onSubmit={codeForm.handleSubmit(onEditMatterCode)}>
              <div className="space-y-4">
                <FormField
                  control={codeForm.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">Matter Code</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter matter code..." className="w-full" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsCodeModalOpen(false);
                    codeForm.reset({ code: matter.code || '' });
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={isLoading}>
                  {isLoading ? 'Updating...' : matter.code ? 'Update Code' : 'Add Code'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
